import '@/App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { WebLayout } from '@/components/layout/WebLayout';
import { RouteGuard } from '@/components/layout/RouteGuard';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import MyVIA from '@/pages/MyVIA';
import KnowledgeHub from '@/pages/kb/KnowledgeHub';
import KBSearch from '@/pages/kb/KBSearch';
import KBCategory from '@/pages/kb/KBCategory';
import KBArticle from '@/pages/kb/KBArticle';
import PoliticaIA from '@/pages/kb/PoliticaIA';
import Dashboard from '@/pages/Dashboard';
import PortfolioHub from '@/pages/PortfolioHub';
import NewInitiative from '@/pages/NewInitiative';
import InitiativeIntake from '@/pages/InitiativeIntake';
import QuickInitiativeForm from '@/pages/QuickInitiativeForm';
import ConversationIntake from '@/pages/ConversationIntake';
import InitiativeDetail from '@/pages/InitiativeDetail';
import AssessmentRun from '@/pages/AssessmentRun';
import Committee from '@/pages/Committee';
import CommitteeConfig from '@/pages/CommitteeConfig';
import ShadowAI from '@/pages/ShadowAI';
import Reevaluations from '@/pages/Reevaluations';
import ValueOffice from '@/pages/ValueOffice';
import Tasks from '@/pages/Tasks';
import Audit from '@/pages/Audit';
import Admin from '@/pages/Admin';
import OperatingSystem from '@/pages/OperatingSystem';
import AdoptionHub from '@/pages/AdoptionHub';
import DeveloperGuide from '@/pages/DeveloperGuide';
import SafeSandbox from '@/pages/SafeSandbox';
import Incidents from '@/pages/Incidents';
import Assurance from '@/pages/Assurance';
import BrandCenter from '@/pages/BrandCenter';
import ToolkitHub from '@/pages/toolkit/ToolkitHub';
import ApplicationPlatform from '@/pages/ApplicationPlatform';
import ApplicationAdmin from '@/pages/ApplicationAdmin';
import ApplicationWizard from '@/pages/ApplicationWizard';
import Insights from '@/pages/Insights';
import InsightsAgents from '@/pages/InsightsAgents';

// Lazy-loaded portfolio pages
const DemandIntake = lazy(() => import('@/pages/DemandIntake'));
const Capabilities = lazy(() => import('@/pages/Capabilities'));

const BusinessRelationships = lazy(() => import('@/pages/BusinessRelationships'));
const ProviderAlerts = lazy(() => import('@/pages/ProviderAlerts'));
const GovernanceAgenda = lazy(() => import('@/pages/GovernanceAgenda'));
const FinancialExecutive = lazy(() => import('@/pages/FinancialExecutive'));
const ValueHarvest = lazy(() => import('@/pages/ValueHarvest'));

// Lazy-loaded admin pages
const Mantenedores = lazy(() => import('@/pages/Mantenedores'));

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PermissionProtected = ({ permission, children }) => {
  const { can } = useAuth();
  if (!can?.[permission]) return <Navigate to="/" replace />;
  return children;
};

// Guard temporal para VIA Insights: por ahora visible solo para administradores
// (mismo criterio que el bloque Administración del menú). Cuando se construya el
// sistema de permisos finos (RA-05: agents.use.<dominio>), se reemplaza por un
// PermissionProtected basado en ese permiso.
const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Web layout — full-width, top nav with flyout menus */}
          <Route
            element={
              <Protected>
                <RouteGuard>
                  <WebLayout />
                </RouteGuard>
              </Protected>
            }>
            <Route path="/" element={<MyVIA />} />
            <Route path="/kb" element={<KnowledgeHub />} />
            <Route path="/kb/buscar" element={<KBSearch />} />
            <Route path="/kb/recurso/politica-ia" element={<PoliticaIA />} />
            <Route path="/kb/article/:slug" element={<KBArticle />} />
            <Route path="/kb/:categorySlug" element={<KBCategory />} />
            <Route path="/brand" element={<BrandCenter />} />
            <Route path="/toolkit/review-queue" element={<ToolkitHub />} />
            <Route path="/toolkit/catalogo" element={<Navigate to="/toolkit/modelos" replace />} />
            <Route path="/toolkit/:type/:assetId" element={<ToolkitHub />} />
            <Route path="/toolkit/:type" element={<ToolkitHub />} />
            <Route path="/toolkit" element={<Navigate to="/toolkit/prompts" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/*
              El chat /insights es para USUARIOS FINALES: NO lleva AdminOnly. El
              acceso lo controla RouteGuard por route_catalog/tenant_routes
              (/insights ya está registrada por la migración 057). El acceso fino
              por grupos (RA-05: agents.use.<dominio>) es trabajo posterior.
            */}
            <Route path="/insights" element={<Insights />} />
            {/*
              Pantalla unica de configuracion de agentes (datos, descripciones e
              instrucciones). Solo administradores. Las rutas historicas
              /insights/admin y /insights/catalog redirigen aqui.
            */}
            <Route
              path="/insights/agents"
              element={
                <AdminOnly>
                  <InsightsAgents />
                </AdminOnly>
              }
            />
            <Route path="/insights/admin" element={<Navigate to="/insights/agents" replace />} />
            <Route path="/insights/catalog" element={<Navigate to="/insights/agents" replace />} />
            <Route path="/registro" element={<PortfolioHub />} />
            <Route path="/registro/nueva" element={<Navigate to="/demanda/nueva" replace />} />
            <Route
              path="/registro/nueva/conversacion"
              element={<Navigate to="/demanda/nueva/conversacion" replace />}
            />
            <Route path="/registro/nueva/rapido" element={<Navigate to="/demanda/nueva/rapido" replace />} />
            <Route path="/registro/nueva/completo" element={<NewInitiative />} />
            <Route path="/iniciativas/:id" element={<InitiativeDetail />} />
            <Route path="/iniciativas/:id/evaluar" element={<AssessmentRun />} />
            <Route path="/comite" element={<Committee />} />
            <Route path="/comite/configuracion" element={<CommitteeConfig />} />
            <Route path="/shadow-ai" element={<ShadowAI />} />
            <Route path="/reevaluaciones" element={<Reevaluations />} />
            <Route path="/valor" element={<ValueOffice />} />
            <Route path="/madurez" element={<OperatingSystem />} />
            <Route path="/adopcion" element={<AdoptionHub />} />
            <Route path="/guia-desarrollo" element={<DeveloperGuide />} />
            <Route path="/herramientas" element={<AdoptionHub />} />
            <Route path="/sandbox" element={<SafeSandbox />} />
            <Route path="/prompts" element={<Navigate to="/toolkit/prompts" replace />} />
            <Route path="/incidentes" element={<Incidents />} />
            <Route path="/vigilancia" element={<Assurance />} />
            <Route path="/casos-exito" element={<Navigate to="/valor" replace />} />
            <Route path="/tareas" element={<Tasks />} />
            <Route path="/registros" element={<Navigate to="/toolkit/modelos" replace />} />
            <Route path="/auditoria" element={<Audit />} />
            <Route path="/accesos" element={<Admin />} />
            <Route path="/admin" element={<Navigate to="/accesos" replace />} />
            <Route
              path="/mantenedores"
              element={
                <Suspense fallback={null}>
                  <Mantenedores />
                </Suspense>
              }
            />
            {/* Portfolio — lazy-loaded routes */}
            <Route
              path="/demanda"
              element={
                <Suspense fallback={null}>
                  <DemandIntake />
                </Suspense>
              }
            />
            <Route path="/demanda/nueva" element={<InitiativeIntake />} />
            <Route path="/demanda/nueva/conversacion" element={<ConversationIntake />} />
            <Route path="/demanda/nueva/rapido" element={<QuickInitiativeForm />} />
            <Route
              path="/capacidades"
              element={
                <Suspense fallback={null}>
                  <Capabilities />
                </Suspense>
              }
            />
            <Route path="/roadmap" element={<Navigate to="/registro?vista=roadmap" replace />} />
            <Route path="/tracking" element={<Navigate to="/registro?vista=tracking" replace />} />
            <Route
              path="/relaciones-negocio"
              element={
                <Suspense fallback={null}>
                  <BusinessRelationships />
                </Suspense>
              }
            />
            <Route
              path="/alertas-proveedor"
              element={
                <Suspense fallback={null}>
                  <ProviderAlerts />
                </Suspense>
              }
            />
            <Route
              path="/agenda-gobierno"
              element={
                <Suspense fallback={null}>
                  <GovernanceAgenda />
                </Suspense>
              }
            />
            <Route
              path="/financiero"
              element={
                <Suspense fallback={null}>
                  <FinancialExecutive />
                </Suspense>
              }
            />
            <Route path="/priorizacion" element={<Navigate to="/registro?vista=priorizacion" replace />} />
            <Route
              path="/cosecha"
              element={
                <Suspense fallback={null}>
                  <ValueHarvest />
                </Suspense>
              }
            />
            <Route
              path="/aplicaciones-ia"
              element={
                <PermissionProtected permission="applications">
                  <ApplicationPlatform />
                </PermissionProtected>
              }
            />
            <Route
              path="/aplicaciones-ia/nueva"
              element={
                <PermissionProtected permission="applications">
                  <ApplicationWizard />
                </PermissionProtected>
              }
            />
            <Route
              path="/aplicaciones-ia/:id"
              element={
                <PermissionProtected permission="applications">
                  <ApplicationAdmin />
                </PermissionProtected>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
